import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Row, Col, Spinner } from "react-bootstrap";
import { TabellaListaServer } from '../../../components/Tabella/TabellaListaServer';
import { ChipFilter } from '../../../components/Tabella/ChipFilter';
import { MultiSelectFilter } from '../../../components/Tabella/MultiSelectFilter';
import { RangeFilterButton } from '../../../components/Tabella/RangeFilterButton';
import MyApiClient from '../../../../api-client/MyApiClient';
import { dataItaliana, orario, statoAssemblea, TIPOLOGIE } from './stati';

const PAGE_SIZE = 20;

// La ricerca la tiene la tabella (casella accanto ai filtri): qui solo il resto.
const FILTRI_INIZIALI = { dateFrom: '', dateTo: '', type: '' };

const TIPOLOGIA_OPTIONS = Object.entries(TIPOLOGIE).map(([value, label]) => ({ value, label }));

// Gli stati sono cinque e la macchina li attraversa in sequenza: si mostrano tutti
// come interruttori accanto alla ricerca, invece che in una tendina, perché guardarli
// accesi o spenti dice a colpo d'occhio cosa si sta guardando. Sono a scelta
// multipla, per questo non usano ChipFilter (che ne accende uno solo) ma ne
// riprendono l'aspetto.
const STATI_FILTRO = ['DRAFT', 'CONVENED', 'FIRST_CALL_DESERTED', 'DESERTED', 'MINUTES_DRAFT', 'MINUTES_FINAL'];

export const ElencoAssemblee = () => {

	const navigate = useNavigate();

	const [data_json, setDataJson] = useState([]);
	const [pageCount, setPageCount] = useState(0);
	const [total, setTotal] = useState(0);

	const [filtri, setFiltri] = useState(FILTRI_INIZIALI);
	const [statiSelezionati, setStatiSelezionati] = useState(STATI_FILTRO);

	const [modalShowed, setModalShowed] = useState(false);
	const [modalTitle, setModalTitle] = useState('');
	const [modalText, setModalText] = useState('');
	const [loadingShowed, setLoadingShowed] = useState(false);

	const paginazione = useRef({ pageIndex: 0, pageSize: PAGE_SIZE });
	// Solo l'ultima richiesta conta: filtri ricordati o cambiati in fretta fanno partire più chiamate,
	// e una risposta vecchia arrivata dopo mostrava la lista senza filtri con i chip accesi.
	const ultimaRichiesta = useRef(0);

	const getAuth = () => {
		const tokenDetailsString = sessionStorage.getItem('userDetails');
		if (!tokenDetailsString) return null;
		const tokenDetails = JSON.parse(tokenDetailsString);
		return { token: tokenDetails.idToken, clubId: tokenDetails.localId };
	};

	const mostraErrore = (testo) => {
		setModalTitle("Errore durante l'operazione");
		setModalText(testo);
		setModalShowed(true);
	};

	// Tutti gli stati accesi vuol dire nessun filtro: si evita di mandare al server
	// cinque parametri che non escludono niente.
	const statiDaInviare = useMemo(
		() => (statiSelezionati.length === STATI_FILTRO.length ? undefined : statiSelezionati),
		[statiSelezionati]
	);

	// Filtri passati alla tabella come `externalFilters`: quando cambiano torna da
	// sola alla prima pagina. `nessunoStato` distingue "nessuno acceso" da "tutti".
	const filtriEsterni = useMemo(
		() => ({ ...filtri, status: statiDaInviare, nessunoStato: statiSelezionati.length === 0 }),
		[filtri, statiDaInviare, statiSelezionati]
	);

	// La ricerca (sul testo della convocazione) arriva dalla tabella gia' ritardata
	// di 600ms e solo da 3 caratteri in su.
	const caricaDati = useCallback(async (pageIndex, pageSize, search, filtriApplicati) => {
		const auth = getAuth();
		if (!auth) return;
		const richiesta = ++ultimaRichiesta.current;

		// Nessuno stato acceso: la risposta sarebbe vuota per costruzione, tanto vale
		// non disturbare il server.
		if (filtriApplicati.nessunoStato) {
			setDataJson([]);
			setTotal(0);
			setPageCount(0);
			return;
		}

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const risultato = await apiClient.listAssemblies(auth.token, {
			offset: pageIndex * pageSize,
			limit: pageSize,
			search,
			dateFrom: filtriApplicati.dateFrom,
			dateTo: filtriApplicati.dateTo,
			type: filtriApplicati.type,
			status: filtriApplicati.status
		});
		if (richiesta !== ultimaRichiesta.current) return;
		setLoadingShowed(false);

		if (!risultato || risultato.error) {
			mostraErrore("Non è stato possibile caricare le assemblee: " + (risultato ? risultato.error : 'errore generico'));
			return;
		}

		const righe = (risultato.items || []).map(assemblea => ({
			id: assemblea.id,
			number: assemblea.number,
			title: assemblea.title,
			type: assemblea.type,
			status: assemblea.status,
			firstCall: assemblea.firstCall,
			original: assemblea
		}));

		setDataJson(righe);
		setTotal(risultato.total || 0);
		setPageCount(Math.ceil((risultato.total || 0) / pageSize));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchData = useCallback(({ pageIndex, pageSize, globalFilter, externalFilters }) => {
		paginazione.current = { pageIndex, pageSize };
		caricaDati(pageIndex, pageSize, (globalFilter || '').trim(), externalFilters || FILTRI_INIZIALI);
	}, [caricaDati]);

	const aggiornaFiltro = (campo, valore) => setFiltri(prec => ({ ...prec, [campo]: valore }));


	const apri = (id, tab = 'dati') => navigate(`/struttura/assemblee/${id}?tab=${tab}`);

	const data_column = [
		{
			Header: 'Nome',
			accessor: 'title',
			Cell: ({ row }) => <span style={{ fontWeight: 600 }}>{row.original.title}</span>,
			disableFilters: true
		},
		{
			Header: 'Tipologia',
			accessor: 'type',
			Cell: ({ row }) => TIPOLOGIE[row.original.type] || row.original.type,
			disableFilters: true
		},
		{
			Header: 'N.',
			accessor: 'number',
			Cell: ({ row }) => (
				<span className="text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
					{row.original.number ?? '—'}
				</span>
			),
			disableFilters: true
		},
		{
			Header: 'Data',
			accessor: 'firstCall',
			Cell: ({ row }) => dataItaliana(row.original.firstCall?.date),
			disableFilters: true
		},
		{
			Header: 'Orario',
			accessor: 'orario',
			Cell: ({ row }) => orario(row.original.firstCall),
			disableFilters: true
		},
		{
			Header: 'Luogo',
			accessor: 'luogo',
			Cell: ({ row }) => row.original.firstCall?.location || '—',
			disableFilters: true
		},
		{
			Header: 'Stato',
			accessor: 'status',
			Cell: ({ row }) => {
				const stato = statoAssemblea(row.original.status);
				return <span className={`sid-pill ${stato.pill}`}>{stato.label}</span>;
			},
			disableFilters: true
		},
	];

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status">
						<span className="sr-only">Loading...</span>
					</Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={modalShowed} centered>
				<Modal.Header>
					<Modal.Title>{modalTitle}</Modal.Title>
					<Button onClick={() => setModalShowed(false)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>
					<Row><Col className="col-xl-64">{modalText}</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-between">
					<Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<TabellaListaServer
				openColumn={'title'}
				openRow={(row) => apri(row.id)}
				pageList={true}
				cardTitle={'Assemblee'}
				sidLayout={true}
				disableSortBy={true}
				headerActions={
					<Button
						className="btn btn-primary btn-add"
						style={{ textTransform: 'none' }}
						onClick={() => navigate('/struttura/assemblee/nuova?tab=dati')}
					>
						+ Crea nuova
					</Button>
				}
				filterControl={
					<div className="sid-chip-filters">
						<RangeFilterButton
							label="Periodo"
							ranges={[{ key: 'periodo', label: "Data dell'assemblea", type: 'date' }]}
							value={{ periodo: { da: filtri.dateFrom, a: filtri.dateTo } }}
							onApply={(nuovi) => setFiltri(prec => ({ ...prec, dateFrom: nuovi.periodo?.da || '', dateTo: nuovi.periodo?.a || '' }))}
						/>
						<ChipFilter label="Tipologia" options={TIPOLOGIA_OPTIONS} value={filtri.type} onChange={(valore) => aggiornaFiltro('type', valore)} />
						<MultiSelectFilter
							label="Stato"
							options={STATI_FILTRO.map(stato => ({ value: stato, label: statoAssemblea(stato).label }))}
							selected={statiSelezionati}
							onChange={setStatiSelezionati}
						/>
					</div>
				}
				externalFilters={filtriEsterni}
				totalItems={total}
				hasSearch={true}
				searchPlaceholder={'Cerca per testo della convocazione'}
				hasFilter={false}
				hasAddMore={false}
				data_column={data_column}
				data_json={data_json}
				manualPagination={true}
				pageCount={pageCount}
				fetchData={fetchData}
				sort_by={'number'}
				sort_type={'desc'}
				manualGlobalFilter={true}
				manualFilters={false}
			/>
		</>
	);
};

export default ElencoAssemblee;
